import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';
import { TargetingValue } from './TargetingPicker';

/**
 * A release's unsaved choices, kept while its announcement is edited on «הודעות למשתמשים» and put
 * back when the editor returns to the release (HAT-1489). Per tab (sessionStorage), per flag, and
 * only for the round trip: it is read once, on the way back, and then dropped.
 */
export interface ReleaseDraft {
  status: FeatureLifecycleStatus;
  description: string;
  targeting: TargetingValue;
  savedAt: number;
}

/** A draft older than this is a leftover, not a round trip. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

const keyOf = (featureKey: string) => `featureFlags.releaseDraft.${featureKey}`;

export const saveReleaseDraft = (featureKey: string, draft: Omit<ReleaseDraft, 'savedAt'>) => {
  try {
    sessionStorage.setItem(keyOf(featureKey), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // A browser without storage returns to the saved release; nothing worse.
  }
};

export const takeReleaseDraft = (featureKey: string): ReleaseDraft | null => {
  try {
    const raw = sessionStorage.getItem(keyOf(featureKey));
    sessionStorage.removeItem(keyOf(featureKey));
    if (!raw) return null;
    const draft = JSON.parse(raw) as ReleaseDraft;
    return Date.now() - draft.savedAt <= MAX_AGE_MS ? draft : null;
  } catch {
    return null;
  }
};

/** The release dialog to come back to, with the draft put back. */
export const releaseReturnUrl = (featureKey: string) => `/management/feature-flags?flag=${encodeURIComponent(featureKey)}&draft=1`;
